import type { SupportedLanguage } from '@kundaliai/shared';

const translations = {
  en: {
    // Nav items
    myJourney: 'My Journey',
    myKundali: 'My Kundali',
    predictions: 'Predictions',
    reports: 'Reports',
    compatibility: 'Compatibility',
    dosha: 'Dosha',
    remedies: 'Remedies',
    pricing: 'Pricing',
    astrology: 'Astrology',
    blog: 'Blog',
    about: 'About',
    startFree: 'Start free reading',
    // Auth
    signIn: 'Sign In',
    signUp: 'Get Started',
    signOut: 'Sign Out',
    // Header
    credits: 'Credits',
    signInToAccess: 'Sign in to access this feature',
    // Chat
    newChat: 'New Chat',
    chat: 'Chat',
    typeMessage: 'Type your message...',
    send: 'Send',
    // Language
    language: 'Language',
    english: 'English',
    hindi: 'Hindi',
    marathi: 'Marathi',
    // Common
    loading: 'Loading...',
    error: 'Something went wrong',
    retry: 'Retry',
    cancel: 'Cancel',
    save: 'Save',
    delete: 'Delete',
    close: 'Close',
    back: 'Back',
    next: 'Next',
    profile: 'Profile',
    settings: 'Settings',
  },
  hi: {
    // Nav items
    myJourney: 'मेरी यात्रा',
    myKundali: 'मेरी कुंडली',
    predictions: 'भविष्यवाणी',
    reports: 'रिपोर्ट',
    compatibility: 'गुण मिलान',
    dosha: 'दोष',
    remedies: 'उपाय',
    pricing: 'मूल्य',
    astrology: 'ज्योतिष',
    blog: 'ब्लॉग',
    about: 'हमारे बारे में',
    startFree: 'मुफ़्त रीडिंग शुरू करें',
    // Auth
    signIn: 'साइन इन',
    signUp: 'शुरू करें',
    signOut: 'साइन आउट',
    // Header
    credits: 'क्रेडिट',
    signInToAccess: 'इस सुविधा का उपयोग करने के लिए साइन इन करें',
    // Chat
    newChat: 'नई चैट',
    chat: 'चैट',
    typeMessage: 'अपना संदेश लिखें...',
    send: 'भेजें',
    // Language
    language: 'भाषा',
    english: 'अंग्रेज़ी',
    hindi: 'हिंदी',
    marathi: 'मराठी',
    // Common
    loading: 'लोड हो रहा है...',
    error: 'कुछ गलत हो गया',
    retry: 'पुनः प्रयास',
    cancel: 'रद्द करें',
    save: 'सहेजें',
    delete: 'हटाएं',
    close: 'बंद करें',
    back: 'वापस',
    next: 'आगे',
    profile: 'प्रोफ़ाइल',
    settings: 'सेटिंग्स',
  },
  mr: {
    // Nav items
    myJourney: 'माझा प्रवास',
    myKundali: 'माझी कुंडली',
    predictions: 'भविष्यवाणी',
    reports: 'अहवाल',
    compatibility: 'गुण मिलान',
    dosha: 'दोष',
    remedies: 'उपाय',
    pricing: 'किंमत',
    astrology: 'ज्योतिष',
    blog: 'ब्लॉग',
    about: 'आमच्याबद्दल',
    startFree: 'मोफत वाचन सुरू करा',
    // Auth
    signIn: 'साइन इन',
    signUp: 'सुरू करा',
    signOut: 'साइन आउट',
    // Header
    credits: 'क्रेडिट',
    signInToAccess: 'हे वैशिष्ट्य वापरण्यासाठी साइन इन करा',
    // Chat
    newChat: 'नवीन चॅट',
    chat: 'चॅट',
    typeMessage: 'तुमचा संदेश लिहा...',
    send: 'पाठवा',
    // Language
    language: 'भाषा',
    english: 'इंग्रजी',
    hindi: 'हिंदी',
    marathi: 'मराठी',
    // Common
    loading: 'लोड होत आहे...',
    error: 'काहीतरी चूक झाली',
    retry: 'पुन्हा प्रयत्न करा',
    cancel: 'रद्द करा',
    save: 'जतन करा',
    delete: 'हटवा',
    close: 'बंद करा',
    back: 'मागे',
    next: 'पुढे',
    profile: 'प्रोफाइल',
    settings: 'सेटिंग्ज',
  },
} as const;

export type TranslationKey = keyof typeof translations.en;

export function t(key: TranslationKey, language: SupportedLanguage = 'en'): string {
  return translations[language]?.[key] || translations.en[key] || key;
}

export function useTranslation(language: SupportedLanguage) {
  return {
    t: (key: TranslationKey) => t(key, language),
  };
}

export const LANGUAGE_OPTIONS = [
  { code: 'en' as const, label: 'EN', nativeLabel: 'English', flag: 'A' },
  { code: 'hi' as const, label: 'हि', nativeLabel: 'हिंदी', flag: 'अ' },
  { code: 'mr' as const, label: 'म', nativeLabel: 'मराठी', flag: 'म' },
] as const;
